import React, { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const RIYADH = { lat: 24.7136, lng: 46.6753 };

function branchCoords(branch) {
  const p = branch?.pickup || {};
  const lat = Number(p.latitude);
  const lng = Number(p.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function PickupBranchMap({
  branches = [],
  selectedBranchId = "",
  onSelectBranch,
  editMode = false,
  onPinMove,
  pinTarget = null,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const onSelectRef = useRef(onSelectBranch);
  const onPinMoveRef = useRef(onPinMove);
  const editModeRef = useRef(editMode);
  const suppressRef = useRef(0);

  onSelectRef.current = onSelectBranch;
  onPinMoveRef.current = onPinMove;
  editModeRef.current = editMode;

  const mappable = useMemo(
    () => branches
      .map((branch) => {
        const coords = branchCoords(branch);
        if (!coords) return null;
        return { branch, coords };
      })
      .filter(Boolean),
    [branches]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return undefined;

    const map = L.map(el, { zoomControl: true, attributionControl: true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    map.on("moveend", () => {
      if (!editModeRef.current || !onPinMoveRef.current) return;
      if (suppressRef.current > 0) {
        suppressRef.current -= 1;
        return;
      }
      const center = map.getCenter();
      onPinMoveRef.current(center.lat, center.lng);
    });

    suppressRef.current += 1;
    map.setView([RIYADH.lat, RIYADH.lng], 12);
    mapRef.current = map;

    const resizeTimer = setTimeout(() => map.invalidateSize(), 320);
    return () => {
      clearTimeout(resizeTimer);
      map.remove();
      mapRef.current = null;
      markersRef.current = [];
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const iconHtml = (selected) => `
      <span class="cmsw__branch-pin${selected ? " cmsw__branch-pin--selected" : ""}" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3L4 9v12h6v-7h4v7h6V9l-8-6z"/>
        </svg>
      </span>`;

    mappable.forEach(({ branch, coords }) => {
      const id = String(branch.branchId);
      const selected = id === String(selectedBranchId);
      const marker = L.marker([coords.lat, coords.lng], {
        icon: L.divIcon({
          className: "cmsw__branch-marker",
          html: iconHtml(selected),
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      });
      marker.on("click", () => onSelectRef.current?.(id));
      marker.addTo(map);
      markersRef.current.push(marker);
    });

    if (mappable.length === 1) {
      suppressRef.current += 1;
      map.setView([mappable[0].coords.lat, mappable[0].coords.lng], 14);
    } else if (mappable.length > 1) {
      const bounds = L.latLngBounds(mappable.map(({ coords }) => [coords.lat, coords.lng]));
      suppressRef.current += 1;
      map.fitBounds(bounds, { padding: [28, 28], maxZoom: 14 });
    }

    if (pinTarget && editMode) {
      suppressRef.current += 1;
      map.setView([pinTarget.lat, pinTarget.lng], pinTarget.zoom ?? 15);
    } else if (selectedBranchId) {
      const hit = mappable.find(({ branch }) => String(branch.branchId) === String(selectedBranchId));
      if (hit) {
        suppressRef.current += 1;
        map.setView([hit.coords.lat, hit.coords.lng], 15);
      }
    }
  }, [mappable, selectedBranchId, pinTarget, editMode]);

  if (!mappable.length) return null;

  return (
    <div className="cmsw__pickup-branch-map" dir="ltr">
      <div ref={containerRef} className="cmsw__pickup-branch-map-el" />
      {editMode && pinTarget ? (
        <svg className="cmsw__pickup-branch-pin" viewBox="0 0 34 46" aria-hidden="true">
          <path
            d="M17 0C7.6 0 0 7.6 0 17c0 12.8 17 29 17 29s17-16.2 17-29C34 7.6 26.4 0 17 0z"
            fill="#dc2626"
          />
          <circle cx="17" cy="17" r="6.5" fill="#fff" />
        </svg>
      ) : null}
    </div>
  );
}

export default PickupBranchMap;
export { branchCoords };
